import { SAlert, SBox, SLoader } from "@avalara/skylab-sdk/react";
import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { testIDs } from "../../../end-to-end-tests/constants/testIDs";
import {
    fetchCompanies,
    selectCompanies,
    selectIsLoadingCompanies,
} from "../../app/companyGroupsSlice";
import { loadDataTransfers } from "../../app/dataTransferSlice";
import type { AppDispatch } from "../../app/types";
import toast from "../../hooks/toast";
import { redirectToLoginPage } from "../../shared/sessionUtility";
import { postDataTransferExport } from "./dataTransferApi";
import ExportOptionsForm, { type ExportCompany } from "./ExportOptionsForm";
import { EMPTY_EXPORT_OPTIONS, hasSelectedOption, type ExportOptions } from "./exportOptions";

const [showToast] = toast();

const DataTransferExportPanel: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const companies = useSelector(selectCompanies) as ExportCompany[];
    const isLoadingCompanies = useSelector(selectIsLoadingCompanies) as boolean;
    const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
    const [options, setOptions] = useState<ExportOptions>(EMPTY_EXPORT_OPTIONS);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        dispatch(fetchCompanies());
    }, [dispatch]);

    const canExport = useMemo(
        () => selectedCompanyIds.length > 0 && hasSelectedOption(options) && !isSubmitting,
        [selectedCompanyIds, options, isSubmitting]
    );

    const handleExport = async (): Promise<void> => {
        setIsSubmitting(true);
        try {
            const selected = new Set(selectedCompanyIds);
            await postDataTransferExport({
                companies: (companies ?? [])
                    .filter(company => selected.has(company.value))
                    .map(company => ({ id: Number(company.value), name: company.label })),
                options,
            });
            setSelectedCompanyIds([]);
            setOptions(EMPTY_EXPORT_OPTIONS);
            showToast("success", "Export started. Track its progress in Transfer history.");
            // Jump to page 1: the new job is newest-first, so refreshing the current page could
            // hide it if the user had paged into the history.
            dispatch(loadDataTransfers(1));
        } catch (error) {
            const response = (
                error as { response?: { status?: number; data?: { title?: string } } }
            )?.response;
            if (response?.status === 401) {
                redirectToLoginPage();
            } else {
                showToast("error", response?.data?.title || "Failed to start the export.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <SBox className="margin-bottom-xl" data-testid={testIDs.dataTransfer.exportPanel}>
            <h2 className="margin-top-none">Export data</h2>
            <SAlert
                status="warning"
                noDismiss
                className="margin-bottom-md"
                data-testid={testIDs.dataTransfer.usageWarning}>
                Certificates imported into the destination account count toward its usage. Use this
                feature for mergers and acquisitions, or to move data between a subsidiary or branch
                account.
            </SAlert>
            {isLoadingCompanies ? (
                <SLoader loading aria-live="polite" />
            ) : (
                <ExportOptionsForm
                    companies={companies ?? []}
                    selectedCompanyIds={selectedCompanyIds}
                    options={options}
                    disabled={isSubmitting}
                    onCompaniesChange={setSelectedCompanyIds}
                    onOptionsChange={setOptions}
                />
            )}
            <div className="margin-top-md">
                <button
                    type="button"
                    className={classNames({ primary: true, loading: isSubmitting })}
                    disabled={!canExport}
                    data-testid={testIDs.dataTransfer.exportButton}
                    onClick={handleExport}>
                    Export
                </button>
            </div>
        </SBox>
    );
};

export default DataTransferExportPanel;
